import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import React, { useState } from 'react'
import { Shield, ArrowLeft } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form'
import { AlertWithIcon } from '@/components/ui/alert'
import { useAuth } from '@/contexts/AuthContext'
import { ApiError } from '@/services/api'

export const Route = createFileRoute('/verify')({
  component: VerifyCode,
  validateSearch: (search: Record<string, unknown>): { email: string } => {
    return {
      email: (search.email as string) || '',
    }
  },
})

function VerifyCode() {
  const navigate = useNavigate()
  const { email } = useSearch({ from: '/verify' })
  const { login } = useAuth()
  const [verificationCode, setVerificationCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    if (!verificationCode.trim()) {
      setError('Veuillez entrer le code de vérification')
      return
    }

    if (verificationCode.trim().length !== 6) {
      setError('Le code de vérification doit contenir 6 chiffres')
      return
    }

    setError('')
    setIsLoading(true)

    try {
      // Appel API pour vérifier l'OTP
      await login(email, verificationCode)
      
      // Rediriger vers la page des projets après connexion réussie
      navigate({ to: '/projects' })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Code de vérification invalide ou expiré.')
      } else {
        setError('Une erreur s\'est produite. Veuillez réessayer.')
      }
      console.error('Erreur lors de la vérification de l\'OTP:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const handleBack = () => {
    // Retourner à la page de saisie d'email
    navigate({ to: '/' })
  }

  // Si pas d'email, rediriger vers la page de login
  if (!email) {
    navigate({ to: '/' })
    return null
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-cyan-50 p-4" data-testid="verify-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
          {/* Logo */}
          <div className="flex flex-col items-center justify-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/assets/images/logo.png" 
                alt="Qolaig Logo" 
                className="h-16 w-auto object-contain"
              />
            </div>

            {/* Titre */}
            <div className="text-center mb-2">
              <h1 className="text-2xl font-semibold text-gray-800">
                Portail Client
              </h1>
            </div>

            {/* Sous-titre */}
            <p className="text-center text-gray-500 text-sm">
              Accès sécurisé à vos projets et métriques
            </p>
          </div>

          {/* Section Code de vérification */}
          <div className="space-y-5">
            {/* En-tête avec icône Shield */}
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-gray-400" />
              <h2 className="text-sm font-medium text-gray-700">
                Code de vérification
              </h2>
            </div>

            {/* Message email */}
            <p className="text-sm text-gray-500">
              Code envoyé à {email}
            </p>

            {/* Affichage des erreurs */}
            {error && (
              <AlertWithIcon
                variant="destructive"
                description={error}
                showIcon={true}
              />
            )}

            {/* Champ de saisie du code */}
            <FormField
              label=""
              error={error}
            >
              <Input
                type="text"
                value={verificationCode}
                onChange={(e) => {
                  // Ne permettre que les chiffres
                  const value = e.target.value.replace(/\D/g, '')
                  setVerificationCode(value)
                  if (error) setError('')
                }}
                onKeyPress={handleKeyPress}
                placeholder="123456"
                maxLength={6}
                className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent text-center text-xl tracking-[0.3em] font-semibold py-3"
              />
            </FormField>

            {/* Bouton Se connecter */}
            <Button
              onClick={handleSubmit}
              isLoading={isLoading}
              className="w-full bg-gradient-to-r from-cyan-400 to-cyan-500 text-white font-medium py-3 rounded-lg hover:from-cyan-500 hover:to-cyan-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 disabled:transform-none"
            >
              Se connecter
            </Button>

            {/* Lien Retour */}
            <button
              onClick={handleBack}
              className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center justify-center gap-1.5 py-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
          </div>

          {/* Message d'information */}
          <div className="pt-4 border-t border-gray-100">
            <p className="text-xs text-center text-gray-500">
              Le code de vérification a été envoyé à votre adresse email
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
